// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * I-9 Match Result Badges
 *
 * Clickable badges showing purge eligibility and match details with detail popovers.
 */
import type { FC } from 'react'
import {
  Badge,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Checkbox,
} from '@/components/ui'
import { CheckCircle2, XCircle, Calendar, Clock, Trash2, AlertTriangle } from 'lucide-react'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface I9MatchResult {
  matchScore: number
  ssnMatch: boolean
  dobMatch: boolean
  lastNameMatch: boolean
  firstNameMatch: boolean
  employeeFound: boolean
  multipleRecords: boolean  // HR lookup matched more than one person — not unique, needs review
  isCurrentEmployee: boolean
  retentionExpires: string | null
  eligibleForDeletion: boolean
  extractedName?: string | null  // Name from OCR (Last, First)
  umdData?: {
    uid?: number | null
    umdName?: string | null  // Name from UMD HR (Last, First)
    separationDate?: string | null
    hireDate?: string | null
  }
  extractionCost?: number  // LLM cost for OCR ($)
  error?: string | null  // Extraction or matching error
}

interface PurgeableBadgeProps {
  match: I9MatchResult | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateStr
  }
}

function getTimeSinceSeparation(separationDate: string | null | undefined): string {
  if (!separationDate) return '—'
  try {
    const sep = new Date(separationDate)
    const now = new Date()
    const diffMs = now.getTime() - sep.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const years = Math.floor(diffDays / 365)
    const months = Math.floor((diffDays % 365) / 30)

    if (years > 0) {
      return `${years}y ${months}m ago`
    }
    return `${months}m ago`
  } catch {
    return '—'
  }
}

// Field-match details shared by both PurgeableBadge popovers (active and separated).
const MatchFieldsSection: FC<{ match: I9MatchResult }> = ({ match }) => {
  const fields = [
    { label: 'SSN', matched: match.ssnMatch },
    { label: 'Date of Birth', matched: match.dobMatch },
    { label: 'Last Name', matched: match.lastNameMatch },
    { label: 'First Name', matched: match.firstNameMatch },
  ]

  return (
    <div className="pt-2 border-t space-y-2">
      {match.multipleRecords && (
        <div className="flex items-start gap-2 rounded bg-amber-50 dark:bg-amber-950/40 p-2 text-xs text-amber-800 dark:text-amber-200">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>HR returned multiple people for this lookup. Shown match may not be the right person — verify by hand.</span>
        </div>
      )}
      <div className="font-medium text-sm">Field Matches ({match.matchScore}%)</div>
      <div className="space-y-2">
        {fields.map((field) => (
          <div key={field.label} className="flex items-center gap-2">
            <Checkbox checked={field.matched} disabled className="pointer-events-none" />
            <span className="text-sm">{field.label}</span>
            {field.matched ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 ml-auto" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-muted-foreground ml-auto" />
            )}
          </div>
        ))}
      </div>
      <div className="text-xs text-muted-foreground">
        Lookup by SSN, falling back to name + DOB · Score: SSN + First + Last + DOB (equal weight)
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Purgeable Badge
// ─────────────────────────────────────────────────────────────────────────────

export const PurgeableBadge: FC<PurgeableBadgeProps> = ({ match }) => {
  if (match?.error) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Badge variant="destructive" className="cursor-pointer hover:opacity-80">
            Error
          </Badge>
        </PopoverTrigger>
        <PopoverContent className="w-64">
          <div className="space-y-2">
            <div className="font-medium text-sm flex items-center gap-2">
              <XCircle className="h-4 w-4 text-destructive" />
              Extraction Error
            </div>
            <p className="text-sm text-muted-foreground">{match.error}</p>
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  if (!match || !match.employeeFound) {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        —
      </Badge>
    )
  }

  // Current employee - can't purge, show retention rule in popover
  if (match.isCurrentEmployee) {
    const hireDate = match.umdData?.hireDate

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Badge
            variant="default"
            className="cursor-pointer hover:opacity-80 bg-green-600 hover:bg-green-700"
          >
            Active
            {match.multipleRecords && <AlertTriangle className="h-3 w-3 ml-1 text-amber-300" />}
          </Badge>
        </PopoverTrigger>
        <PopoverContent className="w-72">
          <div className="space-y-3">
            <div className="font-medium text-sm flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              Active Employee
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Hire date:</span>
                <span>{formatDate(hireDate)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Termination date:</span>
                <span>None</span>
              </div>
            </div>

            <div className="pt-2 border-t">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Federal I-9 Retention Rule
              </div>
              <div className="text-xs text-muted-foreground font-mono bg-muted/50 p-2 rounded">
                MAX(hire + 3 years, separation + 1 year)
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Active employees do not meet purge criteria. I-9 must be retained until the later of 1 year after separation or 3 years after hire date.
              </div>
            </div>

            <MatchFieldsSection match={match} />
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  const separationDate = match.umdData?.separationDate
  const hireDate = match.umdData?.hireDate
  const timeSince = getTimeSinceSeparation(separationDate)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge
          variant={match.eligibleForDeletion ? 'default' : 'outline'}
          className={`cursor-pointer hover:opacity-80 ${
            match.eligibleForDeletion ? 'bg-green-600 hover:bg-green-700' : ''
          }`}
        >
          {match.eligibleForDeletion ? (
            <>
              <Trash2 className="h-3 w-3 mr-1" />
              Purgeable
            </>
          ) : (
            'Retain'
          )}
          {match.multipleRecords && <AlertTriangle className="h-3 w-3 ml-1 text-amber-300" />}
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <div className="space-y-3">
          <div className="font-medium text-sm flex items-center gap-2">
            {match.eligibleForDeletion ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                Eligible for Purge
              </>
            ) : (
              <>
                <Clock className="h-4 w-4 text-amber-500" />
                Retention Required
              </>
            )}
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Separated:</span>
              <span>{formatDate(separationDate)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Time since:</span>
              <span>{timeSince}</span>
            </div>
            {match.retentionExpires && (
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Retention expires:</span>
                <span>{formatDate(match.retentionExpires)}</span>
              </div>
            )}
          </div>

          <div className="pt-2 border-t">
            <div className="text-xs font-medium text-muted-foreground mb-1">
              Federal I-9 Retention Rule
            </div>
            <div className="text-xs text-muted-foreground font-mono bg-muted/50 p-2 rounded">
              MAX(hire + 3 years, separation + 1 year)
            </div>
            {hireDate && separationDate && (
              <div className="text-xs text-muted-foreground mt-2">
                Hire: {formatDate(hireDate)} → {formatDate(hireDate ? new Date(new Date(hireDate).getTime() + 3 * 365 * 24 * 60 * 60 * 1000).toISOString() : null)}
                <br />
                Sep: {formatDate(separationDate)} → {formatDate(separationDate ? new Date(new Date(separationDate).getTime() + 1 * 365 * 24 * 60 * 60 * 1000).toISOString() : null)}
              </div>
            )}
          </div>

          <MatchFieldsSection match={match} />
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Employee Name Badge
// ─────────────────────────────────────────────────────────────────────────────

interface EmployeeNameBadgeProps {
  match: I9MatchResult | null
  optixName: string | null  // Name from Optix table
}

export const EmployeeNameBadge: FC<EmployeeNameBadgeProps> = ({ match, optixName }) => {
  // Priority: OCR extracted name > Optix name > fallback
  const displayName = match?.extractedName || optixName || '—'
  const umdName = match?.umdData?.umdName
  const hasMatch = match?.employeeFound

  // If no match result yet, just show the Optix name (or dash)
  if (!match) {
    return <span className="text-muted-foreground">{optixName || '—'}</span>
  }

  // If we have a match with UMD name, show popover comparing names
  if (hasMatch && umdName) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <span className="cursor-pointer hover:underline text-blue-600 dark:text-blue-400">
            {displayName}
          </span>
        </PopoverTrigger>
        <PopoverContent className="w-72">
          <div className="space-y-3">
            <div className="font-medium text-sm">Name Comparison</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-20">OCR:</span>
                <span>{match.extractedName || '—'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-20">UMD HR:</span>
                <span className="font-medium">{umdName}</span>
              </div>
              {optixName && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-20">Optix:</span>
                  <span>{optixName}</span>
                </div>
              )}
            </div>
            <div className="pt-2 border-t text-xs text-muted-foreground">
              Displaying OCR-extracted name. Click Employment Status badge for field details.
            </div>
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  // No match - show extracted name if available, otherwise Optix name
  return <span>{displayName}</span>
}
